/**
 * The roster and the profile — the client half (§ F119/§ F120; § F201 retired
 * seats and guests: a signed-in device is the claim).
 *
 * Thin on purpose: the rules live on the server (`src/routes/player-profile.ts`).
 */

import { authHeaders } from "./player";

export interface AvatarValue {
  kind: "preset" | "drawn";
  preset?: string | null;
  drawing?: unknown;
}

export interface RosterPlayer {
  id: string;
  /** The internal name. Never changes. */
  name: string;
  /** What they are CALLED — `display_name`, falling back to `name`. */
  display_name: string;
  avatar: AvatarValue | null;
  /** § F122 — the chosen colours, or null for the derived session tone. */
  color: string | null;
  ink: string | null;
}

async function json<T>(res: Response): Promise<{ ok: boolean; status: number; data?: T; error?: string }> {
  try {
    const body = (await res.json()) as { success: boolean; data?: T; error?: string };
    return { ok: res.ok && body.success, status: res.status, data: body.data, error: body.error };
  } catch {
    return { ok: false, status: res.status, error: "Bad response" };
  }
}

/**
 * § F201 — the people the player playing now can SEE: themself and everyone
 * they share a group with. Every picker in the app reads this one list.
 */
export async function fetchRoster(): Promise<RosterPlayer[]> {
  const res = await fetch("/api/trivia-night/roster", { headers: authHeaders() });
  const body = await json<{ players: RosterPlayer[] }>(res);
  if (!body.ok || !body.data) throw new Error(body.error ?? "Couldn't load the player list");
  return body.data.players;
}

/**
 * § F122 — every surface that DRAWS a player listens for this.
 *
 * Jake: *"When I pick a new avatar I don't see it reflected in the top-right on
 * the 'pick avatar' screen."* The header chip reads the roster once, so a face
 * chosen on the page under it went unnoticed until the next navigation. A
 * profile save now says so, and anything showing that player re-reads.
 */
export const PROFILE_EVENT = "gtw:profile";

export function subscribeProfile(onChange: () => void): () => void {
  window.addEventListener(PROFILE_EVENT, onChange);
  return () => window.removeEventListener(PROFILE_EVENT, onChange);
}

/** Rename, set an avatar, or both. `avatar: null` clears it. */
export async function saveProfile(
  player: { id: string; token: string },
  changes: {
    display_name?: string | null;
    avatar?: AvatarValue | null;
    color?: string | null;
    ink?: string | null;
  }
): Promise<string | null> {
  const res = await fetch("/api/trivia-night/profile", {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${player.token}` },
    body: JSON.stringify({ player_id: player.id, ...changes }),
  });
  const body = await json<{ display_name: string }>(res);
  if (!body.ok) throw new Error(body.error ?? "Couldn't save that");
  try {
    window.dispatchEvent(new CustomEvent(PROFILE_EVENT));
  } catch {
    /* non-browser */
  }
  return body.data?.display_name ?? null;
}
